import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Badge from "../components/Badge";
import Section from "../components/Section";
import Seo from "../components/Seo";
import { fadeUp, springSoft, staggerContainer, viewportOnce } from "../lib/motion";
import { site } from "../data/site";

const benefits = [
  {
    title: "Fidélisation des talents",
    text: "Une solution de garde concrète et chaleureuse, plébiscitée par les jeunes parents lors du retour de congé maternité ou paternité.",
  },
  {
    title: "Réduction de l'absentéisme",
    text: "Des places fiables, à proximité des principaux sites d'emploi normands : moins d'imprévus pour vos collaborateurs.",
  },
  {
    title: "Marque employeur",
    text: "Un avantage différenciant à mettre en avant dans vos recrutements et votre politique parentalité.",
  },
  {
    title: "Avantage fiscal puissant",
    text: "Le Crédit d'Impôt Famille (CIF) couvre 50 % du coût des berceaux, complété par la déduction d'IS.",
  },
];

const steps = [
  {
    n: "01",
    title: "Échange & cadrage",
    text: "Nous évaluons ensemble vos besoins : nombre de berceaux, sites concernés, profil de vos collaborateurs.",
  },
  {
    n: "02",
    title: "Convention de réservation",
    text: "Vous réservez un ou plusieurs berceaux dans la crèche La La Land la plus pertinente, sur 12 mois renouvelables.",
  },
  {
    n: "03",
    title: "Vos collaborateurs accueillis",
    text: "Inscription accompagnée, intégration en douceur, suivi pédagogique mensuel et reporting RH dédié.",
  },
];

const stats = [
  { value: "14", label: "enfants maximum par structure" },
  { value: "75 %", label: "d'aides cumulées avec CIF + IS" },
  { value: "5★", label: "note moyenne Google" },
];

export default function Entreprises() {
  const subject = encodeURIComponent("Demande berceaux entreprise — La La Land");
  const body = encodeURIComponent(
    "Bonjour,\n\nNous sommes intéressés par la réservation de berceaux La La Land.\n\n— Société :\n— Nombre de berceaux estimé :\n— Site / ville concernée :\n— Date d'effet souhaitée :\n\nMerci de revenir vers nous.",
  );
  const mailto = `mailto:${site.organization.contactEmail}?subject=${subject}&body=${body}`;

  return (
    <>
      <Seo
        path="/entreprises"
        title="Berceaux entreprises — Réservez pour vos collaborateurs"
        description="Réservez des berceaux La La Land pour vos collaborateurs en Normandie. Avantage social, fidélisation, jusqu'à 75 % d'aides via le Crédit d'Impôt Famille."
      />

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge tone="terra" className="mb-6">
              Solution entreprises
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-ink">
              Offrez une place en crèche à vos collaborateurs.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-ink-soft max-w-xl leading-relaxed">
              Réservez un ou plusieurs berceaux dans notre réseau de micro-crèches en Normandie. Un avantage social concret, fortement subventionné par l'État.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={mailto}>
                <button className="bg-terra hover:bg-terra-hover text-white px-8 py-4 text-lg rounded-xl font-medium transition active:scale-[0.97]">
                  Demander un devis
                </button>
              </a>
              <Link to="/nos-micro-creches">
                <button className="bg-surface border border-edge-strong text-ink hover:bg-muted px-8 py-4 text-lg rounded-xl font-medium transition active:scale-[0.97]">
                  Voir nos crèches
                </button>
              </Link>
            </div>
          </div>
          <div className="bg-gradient-to-br from-honey-soft via-sage-soft to-terra-soft rounded-4xl p-8 md:p-10 shadow-soft-md">
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-3 gap-4"
              variants={staggerContainer(0.12)}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              {stats.map((s) => (
                <motion.div
                  key={s.label}
                  variants={fadeUp}
                  className="bg-surface/90 backdrop-blur rounded-2xl p-5 shadow-soft-sm"
                >
                  <p className="font-display text-3xl md:text-4xl font-semibold text-ink">
                    {s.value}
                  </p>
                  <p className="mt-1 text-sm text-ink-soft leading-snug">
                    {s.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Section
        eyebrow="Bénéfices"
        title="Pourquoi un berceau entreprise change la donne"
        description="Au-delà du coup de pouce financier, c'est un signal fort envoyé à vos collaborateurs parents."
      >
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {benefits.map((b) => (
            <motion.article
              key={b.title}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={springSoft}
              className="bg-surface border border-edge-soft rounded-3xl p-6 shadow-soft-sm transition-shadow hover:shadow-soft-md"
            >
              <h3 className="font-display text-xl font-semibold text-ink leading-tight">
                {b.title}
              </h3>
              <p className="mt-3 text-ink-soft leading-relaxed">{b.text}</p>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      <Section
        eyebrow="Comment ça marche"
        title="Trois étapes pour réserver vos berceaux"
      >
        <motion.div
          className="grid md:grid-cols-3 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {steps.map((s) => (
            <motion.article
              key={s.n}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={springSoft}
              className="bg-surface border border-edge-soft rounded-3xl p-7 shadow-soft-sm transition-shadow hover:shadow-soft-md"
            >
              <p className="font-display text-2xl text-terra font-semibold">
                {s.n}
              </p>
              <h3 className="mt-3 font-display text-xl font-semibold text-ink leading-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-ink-soft leading-relaxed">{s.text}</p>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      <Section
        eyebrow="Fiscalité"
        title="Jusqu'à 75 % du coût pris en charge"
        description="Le berceau entreprise est l'un des avantages les mieux fiscalisés de France. Concrètement, sur 100 € engagés :"
      >
        <motion.div
          className="grid md:grid-cols-3 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <motion.article
            variants={fadeUp}
            whileHover={{ y: -6 }}
            transition={springSoft}
            className="bg-sage-soft rounded-3xl p-7 shadow-soft-sm transition-shadow hover:shadow-soft-md"
          >
            <p className="font-display text-4xl font-semibold text-ink">50 %</p>
            <h3 className="mt-2 font-display text-lg font-semibold text-ink">
              Crédit d'Impôt Famille (CIF)
            </h3>
            <p className="mt-2 text-ink-soft leading-relaxed">
              Article 244 quater F du CGI : la moitié de la dépense vous est remboursée par l'État.
            </p>
          </motion.article>
          <motion.article
            variants={fadeUp}
            whileHover={{ y: -6 }}
            transition={springSoft}
            className="bg-honey-soft rounded-3xl p-7 shadow-soft-sm transition-shadow hover:shadow-soft-md"
          >
            <p className="font-display text-4xl font-semibold text-ink">≈25 %</p>
            <h3 className="mt-2 font-display text-lg font-semibold text-ink">
              Déduction d'IS
            </h3>
            <p className="mt-2 text-ink-soft leading-relaxed">
              Les 50 % restants sont déductibles de l'impôt sur les sociétés au taux normal.
            </p>
          </motion.article>
          <motion.article
            variants={fadeUp}
            whileHover={{ y: -6 }}
            transition={springSoft}
            className="bg-terra-soft rounded-3xl p-7 shadow-soft-sm transition-shadow hover:shadow-soft-md"
          >
            <p className="font-display text-4xl font-semibold text-ink">≈25 %</p>
            <h3 className="mt-2 font-display text-lg font-semibold text-ink">
              Coût net entreprise
            </h3>
            <p className="mt-2 text-ink-soft leading-relaxed">
              Reste à charge réel après aides : un avantage à fort impact, pour un coût mesuré.
            </p>
          </motion.article>
        </motion.div>
        <p className="mt-6 text-sm text-ink-muted max-w-3xl">
          Estimation indicative à valider avec votre expert-comptable. Le CIF est plafonné à 500 000 € par an et par entreprise.
        </p>
      </Section>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-gradient-to-br from-sage to-sage-hover text-white rounded-4xl p-10 md:p-16 shadow-soft-lg">
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">
              Discutons de votre besoin en berceaux.
            </h2>
            <p className="mt-4 text-lg opacity-90 max-w-xl">
              Réponse sous 48h, étude personnalisée et simulation fiscale offerte.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={mailto}>
                <button className="bg-white text-sage-hover px-6 py-3 rounded-xl font-medium hover:bg-cream transition active:scale-[0.97]">
                  Nous écrire
                </button>
              </a>
              <a href={`mailto:${site.organization.contactEmail}`}>
                <button className="bg-white/10 border border-white/30 text-white px-6 py-3 rounded-xl font-medium hover:bg-white/20 transition active:scale-[0.97]">
                  {site.organization.contactEmail}
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
